/**
 * SELECT-convention result mapper (spec §15.4, §15.5): SPARQL JSON rows → graph elements.
 *
 * Pure and deterministic so it can be unit-tested and reused by the explorer page and the
 * `graph_view` block. Styling and labels are applied afterwards from `describe-nodes`.
 */
import { parseIri } from "../lib/iri";

export type Provenance = "asserted" | "computed" | "derived" | "ontology" | "unknown";
export type NodeKind = "block" | "annotation" | "doc" | "concept" | "sequence" | "term" | "external" | "literal";

export interface SparqlTerm {
  type: string;
  value: string;
  datatype?: string;
  "xml:lang"?: string;
}
export type Row = Record<string, SparqlTerm | undefined>;

export interface GraphNode {
  id: string;
  kind: NodeKind;
  label?: string;
  types: string[];
  /** literal-valued properties: predicate IRI → values (spec §15.5 rule 3) */
  props: Record<string, string[]>;
  /** extra result variables seen on rows where this node was the subject */
  extras: Record<string, string[]>;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  predicate: string;
  label?: string;
  graphs: string[];
  provenance: Provenance;
  conf?: number;
  /** a derived edge whose source block changed since extraction (spec §18.6) */
  stale?: boolean;
}

export interface MappedGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
  /** type edges collected into `node.types` (spec §15.5 rule 6) */
  typeEdges: GraphEdge[];
  /** the rows did not bind ?s ?p ?o: render as a table only */
  tableOnly: boolean;
  variables: string[];
  rows: Row[];
  /** above the size guard (spec §15.5 rule 8) */
  tooLarge: boolean;
}

export interface MapOptions {
  showTypeEdges?: boolean;
  /** merge derived edges into an asserted edge with the same (s, p, o) — default on (§15.7) */
  mergeDerived?: boolean;
  maxElements?: number;
  /** derived graphs reported out of date by `/graph/stale-graphs` */
  staleGraphs?: ReadonlySet<string>;
}

export const RDF_TYPE = "http://www.w3.org/1999/02/22-rdf-syntax-ns#type";
const CORE_VARS = new Set(["s", "p", "o", "g", "conf", "sType", "oType", "sLabel", "oLabel", "edgeLabel"]);
export const MAX_ELEMENTS = 3000;

export function provenanceOf(graph: string | undefined): Provenance {
  if (!graph) return "unknown";
  if (graph.startsWith("urn:km:graph:asserted:")) return "asserted";
  if (graph.startsWith("urn:km:graph:computed:")) return "computed";
  if (graph.startsWith("urn:km:graph:derived:")) return "derived";
  if (graph.startsWith("urn:km:graph:ontology:")) return "ontology";
  return "unknown";
}

const PRECEDENCE: Provenance[] = ["asserted", "computed", "derived", "ontology", "unknown"];

/** Strongest provenance over a set of graphs (spec §15.5 rule 5). */
export function combinedProvenance(graphs: Iterable<string>): Provenance {
  let best = PRECEDENCE.length - 1;
  for (const g of graphs) best = Math.min(best, PRECEDENCE.indexOf(provenanceOf(g)));
  return PRECEDENCE[best]!;
}

/** Node kind from the IRI shape; `term` vs `external` is refined later by describe-nodes. */
export function kindOf(id: string): NodeKind {
  const p = parseIri(id);
  if (!p) return "external";
  switch (p.kind) {
    case "block":
    case "structure":
    case "reaction":
    case "participation":
    case "condition":
    case "quantity":
    case "checklist_item":
      return "block";
    case "annotation":
    case "annotation_part":
      return "annotation";
    case "doc":
      return "doc";
    case "concept":
      return "concept";
    case "sequence":
    case "seq_membership":
      return "sequence";
    default:
      return "external";
  }
}

function nodeId(t: SparqlTerm): string {
  return t.type === "bnode" ? `_:${t.value}` : t.value;
}

function literalText(t: SparqlTerm): string {
  const lang = t["xml:lang"];
  return lang ? `${t.value}@${lang}` : t.value;
}

export function mapResults(vars: string[], rows: Row[], opts: MapOptions = {}): MappedGraph {
  const { showTypeEdges = false, mergeDerived = true, maxElements = MAX_ELEMENTS, staleGraphs } = opts;
  const tableOnly = !(vars.includes("s") && vars.includes("p") && vars.includes("o"));
  const empty = { nodes: [], edges: [], typeEdges: [], tableOnly, variables: vars, rows, tooLarge: false };
  if (tableOnly) return empty;

  const nodes = new Map<string, GraphNode>();
  const edges = new Map<string, GraphEdge & { graphSet: Set<string> }>();
  const typeEdges = new Map<string, GraphEdge & { graphSet: Set<string> }>();
  const extraVars = vars.filter((v) => !CORE_VARS.has(v));

  const node = (id: string, label?: string): GraphNode => {
    let n = nodes.get(id);
    if (!n) {
      n = { id, kind: kindOf(id), types: [], props: {}, extras: {} };
      nodes.set(id, n);
    }
    if (label && !n.label) n.label = label;
    return n;
  };

  for (const row of rows) {
    const s = row.s;
    const p = row.p;
    const o = row.o;
    if (!s || !p || !o || s.type === "literal" || s.type === "triple" || o.type === "triple") continue;
    const sn = node(nodeId(s), row.sLabel?.value);
    if (row.sType?.value && !sn.types.includes(row.sType.value)) sn.types.push(row.sType.value);
    for (const v of extraVars) {
      const val = row[v];
      if (!val) continue;
      const list = (sn.extras[v] ??= []);
      if (!list.includes(val.value)) list.push(val.value);
    }
    const graph = row.g?.value;

    if (o.type === "literal" || o.type === "typed-literal") {
      const list = (sn.props[p.value] ??= []);
      const text = literalText(o);
      if (!list.includes(text)) list.push(text);
      continue;
    }

    const target = nodeId(o);
    const tn = node(target, row.oLabel?.value);
    if (row.oType?.value && !tn.types.includes(row.oType.value)) tn.types.push(row.oType.value);

    const isType = p.value === RDF_TYPE;
    if (isType && !sn.types.includes(target)) sn.types.push(target);
    const prov = provenanceOf(graph);
    const key = mergeDerived || prov !== "derived" ? `${sn.id}|${p.value}|${target}` : `${sn.id}|${p.value}|${target}|derived`;
    const bucket = isType ? typeEdges : edges;
    let e = bucket.get(key);
    if (!e) {
      e = { id: key, source: sn.id, target, predicate: p.value, graphs: [], provenance: "unknown", graphSet: new Set() };
      bucket.set(key, e);
    }
    if (graph) e.graphSet.add(graph);
    if (row.edgeLabel?.value) e.label = row.edgeLabel.value;
    const conf = row.conf?.value !== undefined ? Number(row.conf.value) : undefined;
    if (conf !== undefined && !Number.isNaN(conf) && prov === "derived") e.conf = Math.max(e.conf ?? 0, conf);
  }

  const finish = (e: GraphEdge & { graphSet: Set<string> }): GraphEdge => {
    const { graphSet, ...rest } = e;
    const provenance = combinedProvenance(graphSet);
    // only a purely derived edge goes stale: merged with an asserted one, the assertion stands
    const stale = provenance === "derived" && [...graphSet].some((g) => staleGraphs?.has(g));
    return { ...rest, graphs: [...graphSet].sort(), provenance, ...(stale ? { stale } : {}) };
  };
  const edgeList = [...edges.values()].map(finish);
  const typeList = [...typeEdges.values()].map(finish);
  let nodeList = [...nodes.values()];
  if (!showTypeEdges) {
    // type-only targets (classes reached solely through rdf:type) are not drawn as nodes
    const referenced = new Set<string>();
    for (const e of edgeList) {
      referenced.add(e.source);
      referenced.add(e.target);
    }
    const typeTargets = new Set(typeList.map((e) => e.target));
    nodeList = nodeList.filter((n) => referenced.has(n.id) || !typeTargets.has(n.id));
  }
  const visibleEdges = showTypeEdges ? [...edgeList, ...typeList] : edgeList;
  return {
    nodes: nodeList,
    edges: visibleEdges,
    typeEdges: typeList,
    tableOnly,
    variables: vars,
    rows,
    tooLarge: nodeList.length + visibleEdges.length > maxElements,
  };
}
